import { useCallback, useEffect, useState } from "react";

function lastConfigKey(workspaceId: string, machineId: string): string {
  return `ghm:dashboard-lastconfig:${workspaceId}:${machineId}`;
}

export interface ConfigChangeNotice {
  /** true quando o configHash mudou desde a última visita a este workflow. */
  changed: boolean;
  /** Descarta o aviso (até a próxima mudança de configuração). */
  dismiss: () => void;
}

/**
 * Transparência da reconciliação (P0.5). Quando o gateway publica uma nova
 * configuração (configHash diferente), o painel se recompõe a partir do
 * novo meta — widgets e overrides resetam por construção (storage
 * versionado). Isso é correto (hardware é a fonte da verdade), mas
 * silencioso confunde: o operador acha que perdeu trabalho sem motivo.
 * Este hook detecta a troca e deixa a página avisar explicitamente.
 *
 * Registra a última versão vista em `localStorage`; na primeira visita não
 * há aviso. O aviso é transiente (some no reload se não dispensado) — é só
 * uma sinalização, não estado crítico.
 */
export function useConfigChangeNotice(
  workspaceId: string,
  machineId: string,
  currentConfigVersion: string | null,
): ConfigChangeNotice {
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !currentConfigVersion) return;
    const key = lastConfigKey(workspaceId, machineId);
    const previous = window.localStorage.getItem(key);
    if (previous && previous !== currentConfigVersion) {
      setChanged(true);
    }
    window.localStorage.setItem(key, currentConfigVersion);
  }, [workspaceId, machineId, currentConfigVersion]);

  const dismiss = useCallback(() => setChanged(false), []);

  return { changed, dismiss };
}
